import React, { useContext, useEffect, useMemo, useRef } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import {
  ButtonGroup,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import { ControlRoomContext } from './ControlPage'
import AllIncidents from './AllIncidents'
import { IncidentCard, sortActiveIncidents } from './ActiveIncidents'
import { UserContext } from '../../../../App'
import newIncidentSound from '../../../../assets/sounds/newIncident.mp3'
import newCriticalIncidentSound from '../../../../assets/sounds/newCriticalIncident.mp3'

const normalIncidentAudio = new Audio(newIncidentSound)
const criticalIncidentAudio = new Audio(newCriticalIncidentSound)

const NewIncidents = ({
  incidents = [],
  controlRoomRef,
  controlRoomID,
  gridProps = {},
  compact = false,
}) => {
  const { setOpenNewIncident, canDispatch = true } =
    useContext(ControlRoomContext)
  const { user } = useContext(UserContext)
  const [openAllIncidents, setOpenAllIncidents] = React.useState(false)
  const previousIncidentIds = useRef([])

  const sortedIncidents = useMemo(
    () => [...incidents].sort(sortActiveIncidents),
    [incidents],
  )

  useEffect(() => {
    if (!incidents?.length) return

    const currentIds = incidents.map((x) => x.id)
    const newIncidents = incidents.filter(
      (x) => !previousIncidentIds.current.includes(x.id),
    )

    if (previousIncidentIds.current.length > 0) {
      const incidentToAlert = newIncidents.find((incident) => {
        const createdById =
          typeof incident.createdBy === 'object'
            ? incident.createdBy?._id
            : incident.createdBy

        return String(createdById) !== String(user?._id)
      })

      if (incidentToAlert) {
        const audio =
          incidentToAlert.priority === 'Critical'
            ? criticalIncidentAudio
            : normalIncidentAudio

        audio.currentTime = 0
        audio.volume = 0.6
        audio.play().catch(() => {})
      }
    }

    previousIncidentIds.current = currentIds
  }, [incidents, user?._id])

  return (
    <Grid item xs={12} lg={5} {...gridProps}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.96)',
          boxShadow: '0 16px 42px rgba(0,0,0,0.22)',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
            background:
              'linear-gradient(135deg, #3F1728 0%, #85276A 70%, #E32525 160%)',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
              }}
            >
              <NewReleasesIcon />
            </Box>

            <Stack spacing={0}>
              <Typography fontWeight={900} color="white">
                Dispatch List
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.72 }}>
                {incidents.length} awaiting dispatch
              </Typography>
            </Stack>
          </Stack>

          <ButtonGroup>
            <IconButton
              size="small"
              onClick={() => setOpenAllIncidents(true)}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.08)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
              }}
            >
              <ManageSearchIcon />
            </IconButton>

            {canDispatch && (
              <IconButton
                size="small"
                onClick={() => setOpenNewIncident(true)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </ButtonGroup>
        </Stack>

        <Stack
          spacing={1.25}
          sx={{
            p: 2,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {incidents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No incidents awaiting dispatch.
            </Typography>
          ) : (
            sortedIncidents.map((inc) => (
              <IncidentCard
                inc={inc}
                controlRoomRef={controlRoomRef}
                showSideMeta={false}
                showResourceMeta={false}
                key={inc.id}
              />
            ))
          )}
        </Stack>
      </Paper>

      <AllIncidents
        open={openAllIncidents}
        onClose={() => setOpenAllIncidents(false)}
        controlRoomID={controlRoomID}
        controlRoomRef={controlRoomRef}
      />
    </Grid>
  )
}

export default NewIncidents
